import React, { useState } from 'react';
import { Upload, FileText, Settings, Download, MapPin, Wind, Zap } from 'lucide-react';
import FileUpload from './components/FileUpload';
import OptimizationSettings from './components/OptimizationSettings';
import TaskVisualization from './components/TaskVisualization';
import ServerStatus from './components/ServerStatus';
import { OptimizationParams, TaskPoint } from './types';

const App: React.FC = () => {
  const [xctaskFile, setXctaskFile] = useState<File | null>(null);
  const [demFile, setDemFile] = useState<File | null>(null);
 const [useTerrainData, setUseTerrainData] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedTask, setOptimizedTask] = useState<TaskPoint[] | null>(null);
  const [originalTask, setOriginalTask] = useState<TaskPoint[] | null>(null);
  const [useKK7Thermal, setUseKK7Thermal] = useState(true);
  const [serverHealthy, setServerHealthy] = useState<boolean | null>(null);
  const [optimizationParams, setOptimizationParams] = useState<OptimizationParams>({
    glideRatio: 8.0,
    startAltitude: 2500.0,
    clearance: 200.0,
    expectedCeiling: 3000.0,
    corridorWidth: 2500.0,
    baseSpeed: 10.0,
    hotspotSpeedGain: 1.0,
    hotspotReward: 1.0,
    distancePenalty: 1.0,
    maxHotspotsPerLeg: 6,
    insertVias: true,
    optimizationDensity: 'balanced'
  });
  const [optimizedFile, setOptimizedFile] = useState<{ blob: Blob; filename: string } | null>(null);
  const [optimizerInfo, setOptimizerInfo] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [taskVizKey, setTaskVizKey] = useState(0);

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleDownload = () => {
    if (optimizedFile) {
      const url = window.URL.createObjectURL(optimizedFile.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = optimizedFile.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleXcTaskFileChange = async (file: File | null) => {
    setXctaskFile(file);

    // Start over when a new file is selected/dropped
    setOptimizedFile(null);
    setOptimizerInfo(null);
    setOptimizedTask(null);
    setOriginalTask(null);
    setIsOptimizing(false);
    resetView();
    setTaskVizKey(k => k + 1);

    if (file) {
      const parsedTask = await parseXCTaskFile(file);
      if (parsedTask) {
        setOriginalTask(parsedTask);
        setOptimizedTask(parsedTask);
      }
    }
  };

  const parseXCTaskFile = async (file: File) => {
    try {
      const text = await file.text();
      
      // Try to parse as JSON first
      try {
        const jsonData = JSON.parse(text);
        if (jsonData.turnpoints && Array.isArray(jsonData.turnpoints)) {
          return jsonData.turnpoints.map((tp: any, index: number) => ({
            name: tp.waypoint?.name || `WP_${index}`,
            lat: tp.waypoint?.lat || 0,
            lon: tp.waypoint?.lon || 0,
            radius_m: tp.radius || 500,
            kind: tp.type === 'TAKEOFF' ? 'start' : 
                   tp.type === 'ESS' ? 'goal' : 
                   tp.name?.includes('Thermal_') ? 'via' : 'turn'
          }));
        }
      } catch (jsonError) {
        // If JSON parsing fails, try XML parsing (simplified)
        const waypointRegex = /<waypoint[^>]*name="([^"]*)"[^>]*lat="([^"]*)"[^>]*lon="([^"]*)"[^>]*alt="([^"]*)"[^>]*\/>/g;
        const waypoints: TaskPoint[] = [];
        let match;
        
        while ((match = waypointRegex.exec(text)) !== null) {
          waypoints.push({
            name: match[1],
            lat: parseFloat(match[2]),
            lon: parseFloat(match[3]),
            radius_m: 500,
            kind: match[1].includes('TAKEOFF') ? 'start' : 
                   match[1].includes('ESS') ? 'goal' : 'turn'
          });
        }
        
        return waypoints;
      }
    } catch (error) {
      console.error('Error parsing XC task file:', error);
      return null;
    }
  };

  const handleOptimize = async () => {
    if (!xctaskFile) {
      alert('Please upload XC task file');
      return;
    }

    setIsOptimizing(true);
    setOptimizerInfo(null);
    
    // Parse and display the original task
    const parsedTask = await parseXCTaskFile(xctaskFile);
    if (parsedTask) {
      setOriginalTask(parsedTask);
      setOptimizedTask(parsedTask);
    }
    
    const formData = new FormData();
    formData.append('xctask', xctaskFile);
    if (demFile && useTerrainData) {
      formData.append('dem', demFile);
    }
    formData.append('params', JSON.stringify(optimizationParams));
    formData.append('useKK7Thermal', useKK7Thermal.toString());
    formData.append('useTerrainData', useTerrainData.toString());

    try {
      const response = await fetch('/api/optimize', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const info = response.headers.get('X-Optimizer-Info');
        if (info) setOptimizerInfo(info);
        const blob = await response.blob();
        const filename = `optimized_${xctaskFile.name}`;
        
        // Store the optimized file for download
        setOptimizedFile({ blob, filename });
        
        // Parse the response to show visualization
        const text = await blob.text();
        const optimizedTask = await parseXCTaskFile(new File([text], filename));
        if (optimizedTask) {
          setOptimizedTask(optimizedTask);
        }
      } else {
        // Get detailed error message from server
        const errorData = await response.json().catch(() => ({}));
        const serverError = errorData.error || 'Optimization failed';
        const debugInfo = errorData.debug_info;
        
        console.error('Server Error Response:', { status: response.status, errorData, debugInfo });
        
        throw new Error(serverError);
      }
    } catch (error) {
      console.error('Optimization Error:', error);
      
      // Enhanced debugging information
      let errorMessage = 'Optimization failed. Please check your files and try again.\n\n';
      
      if (error instanceof Error) {
        errorMessage += `Error details: ${error.message}\n\n`;
        
        // Add specific debugging info based on error type
        if (error.message.includes('Failed to fetch')) {
          errorMessage += 'Network error: Check internet connection and server status.\n';
        } else if (error.message.includes('Missing required files')) {
          errorMessage += 'File error: Ensure all required files are uploaded.\n';
        } else if (error.message.includes('Invalid file type')) {
          errorMessage += 'File format error: Check file formats (.xctsk, .tif).\n';
        } else if (error.message.includes('parse')) {
          errorMessage += 'File parsing error: XC task file may be corrupted.\n';
        } else if (error.message.includes('timeout')) {
          errorMessage += 'Timeout error: Files may be too large or server busy.\n';
        }
      }
      
      errorMessage += '\nDebugging Information:\n';
      errorMessage += `- XC Task file: ${xctaskFile ? xctaskFile.name : 'Not uploaded'}\n`;
      errorMessage += `- DEM file: ${demFile ? demFile.name : 'Not uploaded'}\n`;
      errorMessage += `- KK7 Thermal: ${useKK7Thermal ? 'Enabled' : 'Disabled'}\n`;
      errorMessage += `- Terrain Data: ${useTerrainData ? 'Enabled' : 'Disabled'}\n`;
      errorMessage += `- Glide Ratio: ${optimizationParams.glideRatio}:1\n`;
      errorMessage += `- Start Altitude: ${optimizationParams.startAltitude}m\n`;
      
      console.error('Detailed Error Info:', {
        error,
        files: {
          xctask: xctaskFile?.name,
          dem: demFile?.name
        },
        settings: {
          useKK7Thermal,
          useTerrainData,
          params: optimizationParams
        }
      });
      
      alert(errorMessage);
    } finally {
      setIsOptimizing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Wind className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">XC Task Optimizer</h1>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Zap className="h-4 w-4" />
              <span>Thermal-Optimized Routes</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - File Upload */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Upload className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Upload Files</h2>
              </div>
              
              <FileUpload
                label="XC Task File (.xctsk)"
                accept=".xctsk,.xctask"
                file={xctaskFile}
                onFileChange={handleXcTaskFileChange}
                icon={<FileText className="h-6 w-6" />}
              />
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
                  <div className="flex items-center space-x-2">
                    <Zap className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900">KK7 Thermal Data</span>
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useKK7Thermal}
                      onChange={(e) => setUseKK7Thermal(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm text-blue-700">Auto-fetch thermal.kk7.ch</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  Automatically fetches thermal data for your task region from KK7
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                  <div className="flex items-center space-x-2">
                    <Wind className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-900">Terrain Data</span>
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useTerrainData}
                      onChange={(e) => setUseTerrainData(e.target.checked)}
                      className="h-4 w-4 text-green-600 rounded"
                    />
                    <span className="text-sm text-green-700">Use terrain data</span>
                  </label>
                </div>
                {useTerrainData && (
                  <FileUpload
                    label="DEM Elevation (GeoTIFF) - Optional"
                    accept=".tif,.tiff"
                    file={demFile}
                    onFileChange={setDemFile}
                    icon={<Wind className="h-6 w-6" />}
                  />
                )}
                {!useTerrainData && (
                  <p className="text-xs text-gray-500">
                    Optimization will use flat terrain assumption (recommended for simple tasks)
                  </p>
                )}
              </div>
            </div>

            <ServerStatus onStatusChange={setServerHealthy} />

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Settings className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Optimization Settings</h2>
              </div>
              
              <OptimizationSettings
                params={optimizationParams}
                onParamsChange={setOptimizationParams}
              />
            </div>
          </div>

          {/* Right Column - Visualization and Results */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Task Visualization</h2>
                {optimizedTask && (
                  <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                    <Download className="h-4 w-4" />
                    <span>Download Result</span>
                  </button>
                )}
              </div>
              
              <TaskVisualization
                key={taskVizKey}
                taskPoints={optimizedTask}
                originalTaskPoints={originalTask}
                isLoading={isOptimizing}
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <button
                onClick={handleOptimize}
                disabled={!xctaskFile || (useTerrainData && !demFile) || isOptimizing || serverHealthy === false}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {serverHealthy === false ? 'Server Offline - Cannot Optimize' : 
                 isOptimizing ? 'Optimizing...' : 
                 serverHealthy === null ? 'Checking Server...' : 'Optimize Task'}
              </button>

              {optimizerInfo && (
                <div className="mt-3 p-3 bg-gray-50 border rounded text-xs text-gray-700 font-mono break-all">
                  {optimizerInfo}
                </div>
              )}
              
              {optimizedFile && (
                <button
                  onClick={handleDownload}
                  className="w-full py-3 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium flex items-center justify-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Optimized Task</span>
                </button>
              )}
              
              {serverHealthy === false && (
                <p className="text-xs text-red-600 mt-2 text-center">
                  The optimization server is not responding. Please check that the Python server is running on port 5000.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;
